import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLocation } from 'react-router-dom';
import { getLangFromPath, lp } from '../../utils/routes';
import FormHoneypot from '@/components/feature/FormHoneypot';
import TurnstileField, {
  type TurnstileFieldHandle,
} from '@/components/feature/TurnstileField';
import { isTurnstileConfigured } from '@/config/turnstileEnv';
import { honeypotFilled } from '@/lib/formSpamGuard';
import { submitEmailLead } from '@/services/submitToKailac';
import {
  KAILAC_PHONE_DISPLAY,
  KAILAC_TEL_HREF,
} from '@/config/contactPhone';

export default function Footer() {
  const { t } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const isEs = lang === 'es';
  const [email, setEmail] = useState('');
  const [subscribed, setSubscribed] = useState(false);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const turnstileRef = useRef<TurnstileFieldHandle>(null);

  const handleSubscribe = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormError(null);
    if (!email) return;
    const fd = new FormData(e.currentTarget);
    if (honeypotFilled(fd)) {
      setSubscribed(true);
      setEmail('');
      return;
    }
    setBusy(true);
    try {
      let captchaVerified = false;
      if (isTurnstileConfigured()) {
        const token = await turnstileRef.current?.ensureToken();
        if (!token) {
          setFormError(t('form_captcha_failed'));
          return;
        }
        captchaVerified = true;
      }
      try {
        await submitEmailLead(email, 'footer_newsletter', {
          recaptchaVerified: captchaVerified,
        });
      } catch (_err) {
        // Silent fail
      }
      turnstileRef.current?.reset();
      setSubscribed(true);
      setEmail('');
    } finally {
      setBusy(false);
    }
  };

  return (
    <footer className="bg-[#F8F3FB] border-t border-[#E9D9F2]">
      {/* Start LLC Featured Banner */}
      <div className="border-b border-[#E9D9F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-6">
          <div className="rounded-xl bg-[#4F2D63] px-6 sm:px-8 py-5 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-white/10 flex-shrink-0 mt-0.5">
                <i className="ri-store-3-line text-white text-lg" />
              </div>
              <div>
                <p className="text-xs text-[#D4B8F0] font-semibold uppercase tracking-widest mb-0.5">
                  {isEs ? 'Para nuevos emprendedores' : 'For new entrepreneurs'}
                </p>
                <p className="text-white font-bold text-base leading-snug">{t('footer_startllc_banner_title')}</p>
                <p className="text-[#D4B8F0] text-sm mt-0.5">{t('footer_startllc_banner_desc')}</p>
              </div>
            </div>
            <Link
              to={isEs ? '/es/inicia-tu-llc' : '/start-your-llc'}
              className="flex-shrink-0 inline-flex items-center gap-1.5 bg-white text-[#4F2D63] font-semibold text-sm px-5 py-2.5 rounded-lg hover:bg-[#F0E8F8] transition-colors cursor-pointer whitespace-nowrap"
            >
              {t('footer_startllc_banner_cta')}
              <i className="ri-arrow-right-line text-sm" />
            </Link>
          </div>
        </div>
      </div>

      {/* Main Footer */}
      <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-14 pb-10">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="lg:col-span-2">
            <Link to={lp('/', lang)} className="flex items-center cursor-pointer mb-5">
              <img
                src={`${import.meta.env.BASE_URL}KAILAC.png`}
                alt="Kailac"
                className="h-10 sm:h-11 w-auto max-w-[160px] object-contain object-left"
              />
            </Link>
            <p className="text-sm text-gray-500 leading-relaxed mb-6 max-w-sm">
              {t('footer_desc')}
            </p>

            {/* Newsletter */}
            <p className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-3">
              {t('footer_newsletter_label')}
            </p>
            {subscribed ? (
              <div className="flex items-center gap-2 text-[#0F766E] text-sm">
                <i className="ri-check-circle-line"></i>
                <span>{t('footer_subscribed')}</span>
              </div>
            ) : (
              <form
                onSubmit={handleSubscribe}
                className="relative flex flex-col gap-3 max-w-md"
                id="footer-newsletter"
              >
                <FormHoneypot />
                <TurnstileField
                  ref={turnstileRef}
                  className="flex w-full justify-start min-h-[65px]"
                />
                <div className="flex flex-wrap gap-2">
                  <input
                    type="email"
                    name="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    placeholder={t('footer_newsletter_placeholder')}
                    required
                    className="flex-1 min-w-[200px] px-3 py-2.5 rounded-lg bg-white border border-[#E9D9F2] text-gray-800 placeholder-gray-400 text-sm focus:outline-none focus:border-[#4F2D63]"
                  />
                  <button
                    type="submit"
                    disabled={busy}
                    className="px-4 py-2.5 rounded-lg bg-[#4F2D63] hover:bg-[#3E234F] text-white text-sm font-semibold cursor-pointer whitespace-nowrap transition-colors disabled:opacity-60"
                  >
                    {t('footer_newsletter_btn')}
                  </button>
                </div>
                {formError ? (
                  <div className="w-full space-y-1">
                    <p className="text-xs text-red-600" role="alert">
                      {formError}
                    </p>
                    {isTurnstileConfigured() ? (
                      <p className="text-xs text-gray-500">{t('form_captcha_hint')}</p>
                    ) : null}
                  </div>
                ) : null}
              </form>
            )}

            {/* Social Links */}
            <div className="flex items-center gap-3 mt-6">
              {[
                { icon: 'ri-linkedin-line', label: 'LinkedIn', href: 'https://linkedin.com' },
                { icon: 'ri-facebook-circle-line', label: 'Facebook', href: 'https://facebook.com' },
                { icon: 'ri-instagram-line', label: 'Instagram', href: 'https://instagram.com' },
              ].map(({ icon, label, href }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="nofollow noreferrer"
                  aria-label={label}
                  className="w-9 h-9 flex items-center justify-center rounded-lg bg-white border border-[#E9D9F2] text-gray-500 hover:text-[#4F2D63] hover:border-[#4F2D63] transition-colors cursor-pointer"
                >
                  <i className={`${icon} text-base`}></i>
                </a>
              ))}
            </div>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-4">
              {t('footer_services')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: t('footer_link_how'), href: isEs ? '/es/como-funciona' : '/how-it-works' },
                { label: t('footer_link_plans'), href: isEs ? '/es/planes' : '/plans' },
                { label: t('footer_link_industries'), href: isEs ? '/es/industrias' : '/industries' },
                { label: t('footer_link_why'), href: isEs ? '/es/por-que-nosotros' : '/why-us' },
                { label: t('footer_link_results'), href: isEs ? '/es/resultados' : '/results' },
              ].map(({ label, href }) => (
                <li key={href}>
                  <Link to={href} className="text-sm text-gray-500 hover:text-[#4F2D63] transition-colors cursor-pointer">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Company */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-4">
              {t('footer_company')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: t('footer_link_about'), href: isEs ? '/es/sobre-nosotros' : '/about' },
                { label: t('footer_link_contact'), href: isEs ? '/es/contacto' : '/contact' },
                { label: t('footer_link_faq'), href: isEs ? '/es/preguntas-frecuentes' : '/faq' },
                { label: t('footer_link_startllc'), href: isEs ? '/es/inicia-tu-llc' : '/start-your-llc' },
              ].map(({ label, href }) => (
                <li key={href}>
                  <Link to={href} className="text-sm text-gray-500 hover:text-[#4F2D63] transition-colors cursor-pointer">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>

            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-4 mt-8">
              {t('footer_resources')}
            </h3>
            <ul className="space-y-3">
              {[
                { label: t('footer_link_blog'), href: isEs ? '/es/blog' : '/blog' },
                { label: t('footer_link_checklist'), href: isEs ? '/es/blog' : '/blog' },
              ].map(({ label, href }) => (
                <li key={label}>
                  <Link to={href} className="text-sm text-gray-500 hover:text-[#4F2D63] transition-colors cursor-pointer">
                    {label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Contact & Trust */}
          <div>
            <h3 className="text-xs font-semibold text-gray-700 uppercase tracking-wider mb-4">
              {t('footer_contact_header')}
            </h3>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2 text-sm text-gray-500">
                <i className="ri-map-pin-line text-[#4F2D63] mt-0.5 flex-shrink-0"></i>
                <span>{t('footer_lang_usa')}</span>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-500">
                <i className="ri-phone-line text-[#4F2D63] mt-0.5 flex-shrink-0"></i>
                <a href={KAILAC_TEL_HREF} className="hover:text-[#4F2D63] transition-colors cursor-pointer">
                  {KAILAC_PHONE_DISPLAY}
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-500">
                <i className="ri-mail-line text-[#4F2D63] mt-0.5 flex-shrink-0"></i>
                <a href="mailto:info@kailac.com" className="hover:text-[#4F2D63] transition-colors cursor-pointer">
                  info@kailac.com
                </a>
              </li>
              <li className="flex items-start gap-2 text-sm text-gray-500">
                <i className="ri-global-line text-[#4F2D63] mt-0.5 flex-shrink-0"></i>
                <span>{t('footer_lang_support')}</span>
              </li>
            </ul>

            <div className="space-y-2">
              {[
                { icon: 'ri-shield-check-line', key: 'footer_trust_1' },
                { icon: 'ri-lock-line', key: 'footer_trust_2' },
                { icon: 'ri-verified-badge-line', key: 'footer_trust_3' },
              ].map(({ icon, key }) => (
                <div key={key} className="flex items-center gap-2">
                  <i className={`${icon} text-[#0F766E] text-sm`}></i>
                  <span className="text-xs text-gray-500">{t(key)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer */}
      <div className="border-t border-[#E9D9F2] max-w-7xl mx-auto px-6 lg:px-8 py-5">
        <p className="text-xs text-gray-400 leading-relaxed">
          {t('footer_disclaimer')}
        </p>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-[#E9D9F2]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 py-4 flex flex-col sm:flex-row items-center justify-between gap-4">
          <p className="text-xs text-gray-400">{t('footer_copyright')}</p>
          <div className="flex items-center gap-5">
            <Link
              to={isEs ? '/es/politica-de-privacidad' : '/privacy-policy'}
              className="text-xs text-gray-400 hover:text-[#4F2D63] transition-colors cursor-pointer"
            >
              {t('footer_link_privacy')}
            </Link>
            <span className="text-gray-300">•</span>
            <Link
              to={isEs ? '/es/terminos-de-servicio' : '/terms-of-service'}
              className="text-xs text-gray-400 hover:text-[#4F2D63] transition-colors cursor-pointer"
            >
              {t('footer_link_terms')}
            </Link>
            <span className="text-gray-300">•</span>
            <Link
              to={isEs ? '/es/aviso-legal' : '/disclaimer'}
              className="text-xs text-gray-400 hover:text-[#4F2D63] transition-colors cursor-pointer"
            >
              {t('footer_link_disclaimer')}
            </Link>
          </div>
        </div>
      </div>
    </footer>
  );
}
